import {colors, radii, tokens} from '@commercial-helios/ui/theme'
import {navigate} from 'gatsby'
import Cookies from 'js-cookie'
import React from 'react'
import styled, {css} from 'styled-components'

import LocalizedLink from '@helios/shared/components/localized-link'
import {
    StyledListboxButton,
    StyledListboxPopover,
    StyledListboxOption,
    StyledListboxInput,
    SelectedOption,
    ListboxList,
    Wrapper,
    Arrow,
} from '@helios/shared/components/select'
import {cookiesMap} from '@helios/shared/config/cookies'
import usePopoverDirection from '@helios/shared/hooks/use-popover-direction'
import flagAT from '@helios/shared/images/icons/flags/flag-at.svg'
import flagBE from '@helios/shared/images/icons/flags/flag-be.svg'
import flagDE from '@helios/shared/images/icons/flags/flag-de.svg'
import flagDK from '@helios/shared/images/icons/flags/flag-dk.svg'
import flagEE from '@helios/shared/images/icons/flags/flag-ee.svg'
import flagES from '@helios/shared/images/icons/flags/flag-es.svg'
import flagFI from '@helios/shared/images/icons/flags/flag-fi.svg'
import flagFR from '@helios/shared/images/icons/flags/flag-fr.svg'
import flagGB from '@helios/shared/images/icons/flags/flag-gb.svg'
import flagIE from '@helios/shared/images/icons/flags/flag-ie.svg'
import flagIT from '@helios/shared/images/icons/flags/flag-it.svg'
import flagLU from '@helios/shared/images/icons/flags/flag-lu.svg'
import flagNL from '@helios/shared/images/icons/flags/flag-nl.svg'
import flagNO from '@helios/shared/images/icons/flags/flag-no.svg'
import flagPT from '@helios/shared/images/icons/flags/flag-pt.svg'
import flagSE from '@helios/shared/images/icons/flags/flag-se.svg'
import defaultLocales from '@helios/shared/locale/locales'
import type {CountryCode} from '@helios/shared/locale/locales'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {useLocale} from '@helios/shared/providers/locale'
import {usePageInfo} from '@helios/shared/providers/page-info'
import {Text, text} from '@helios/shared/styles/typography'
import {ellipsisStyles, focusStyles} from '@helios/shared/styles/utils'
import type Locale from '@helios/shared/types/locale'
import mq from '@helios/shared/utils/media-query'

const flagMap: Record<CountryCode, string> = {
    gb: flagGB,
    dk: flagDK,
    ee: flagEE,
    es: flagES,
    fr: flagFR,
    de: flagDE,
    se: flagSE,
    ie: flagIE,
    at: flagAT,
    fi: flagFI,
    be: flagBE,
    nl: flagNL,
    it: flagIT,
    pt: flagPT,
    no: flagNO,
    lu: flagLU,
}

interface LocaleSwitcherProps {
    light?: boolean
    locales?: Locale[]
}

const PLEO_GLOBAL_PAGE_URL = 'https://www.pleo.io/en/global'

const LocaleSwitcher = ({light, locales: customLocales}: LocaleSwitcherProps) => {
    const triggerRef = React.useRef(null)
    const popoverRef = React.useRef(null)
    const direction = usePopoverDirection({triggerRef, popoverRef})
    const currentLocale = useLocale()
    const {pageContext} = usePageInfo()
    const globalStrings = useGlobalStrings()
    const locales = customLocales ?? defaultLocales

    const elementOptions = locales.map((locale) => ({
        label: (
            <Option
                country={locale.country}
                countryName={locale.countryName}
                languageName={locale.languageName}
            />
        ),
        value: locale.urlCode,
        urlCode: locale.urlCode,
    }))

    const getOptionForValue = (value: string | null) => {
        return elementOptions.find((option) => option.value === value)
    }

    const getDestinationPath = (targetLocaleCode: string) => {
        const targetLocale = locales.find((locale) => locale.urlCode === targetLocaleCode)

        // If the selected language is prelaunch, we want to redirect them to the landing page
        if (!targetLocale || targetLocale.isPreLaunch) {
            return `/${targetLocaleCode}`
        }

        return pageContext.paths?.[targetLocale.code] ?? `/${targetLocaleCode}`
    }

    const handleLocaleSwitch = (newLocale: string) => {
        if (newLocale === 'global') {
            if (process.env.ENVIRONMENT === 'website') {
                navigate('/en/global')
            } else {
                window.location.assign(PLEO_GLOBAL_PAGE_URL)
            }
        } else {
            const targetLocale = getDestinationPath(newLocale)

            Cookies.set(cookiesMap.defaultCountry, newLocale)
            navigate(targetLocale)
        }
    }

    const globalLink = () => {
        if (process.env.ENVIRONMENT === 'website') {
            return (
                <LocalizedLink rawPath to={`/en/global`}>
                    {globalStrings.localeSelectorGlobalLabel}
                </LocalizedLink>
            )
        } else {
            return <a href={PLEO_GLOBAL_PAGE_URL}>{globalStrings.localeSelectorGlobalLabel}</a>
        }
    }

    return (
        <Wrapper space={4} stretch>
            <ListboxInput
                value={currentLocale.urlCode}
                onChange={(newLocale) => handleLocaleSwitch(newLocale)}
            >
                <ListboxButton $light={light} $direction={direction} ref={triggerRef}>
                    {(props) => (
                        <>
                            <SelectedOption $isPlaceholder={props.value === ''}>
                                {getOptionForValue(props.value)?.label || (
                                    <span>{currentLocale.countryName}</span>
                                )}
                            </SelectedOption>
                            <Arrow $isExpanded={props.isExpanded} />
                        </>
                    )}
                </ListboxButton>

                <ListboxPopover
                    portal={false}
                    ref={popoverRef}
                    $light={light}
                    $direction={direction}
                >
                    <ListboxList>
                        {elementOptions.map((option) => {
                            const destinationPath = getDestinationPath(option.urlCode)
                            const optionLang = locales.find(
                                (locale) => locale.urlCode === option.urlCode
                            )

                            return (
                                <ListboxOption
                                    $light={light}
                                    key={option.value}
                                    value={option.value}
                                >
                                    <LocalizedLink
                                        rawPath
                                        to={destinationPath}
                                        lang={optionLang?.code}
                                        /**
                                         * We let js handle the navigation in this case,
                                         * so we can set the language cookie and handle keyboard nav.
                                         * We still need to the link for SEO
                                         * */
                                        onClick={(e) => e.preventDefault()}
                                    >
                                        {option.label}
                                    </LocalizedLink>
                                </ListboxOption>
                            )
                        })}
                        {globalStrings.localeSelectorGlobalLabel && (
                            <GlobalOption value="global" onClick={(e) => e.preventDefault()}>
                                {globalLink()}
                            </GlobalOption>
                        )}
                    </ListboxList>
                </ListboxPopover>
            </ListboxInput>
        </Wrapper>
    )
}

interface OptionProps {
    country: CountryCode
    countryName: string
    languageName: string
}

const Option = ({country, countryName, languageName}: OptionProps) => {
    return (
        <StyledOption>
            <img
                src={flagMap[country]}
                alt=""
                loading="lazy"
                width={24}
                height={16}
                aria-hidden="true"
            />

            <StyledOptionCountry>{countryName}</StyledOptionCountry>

            <StyledOptionLanguage variant="label-base" color="lightMuted">
                {languageName}
            </StyledOptionLanguage>
        </StyledOption>
    )
}

interface ListboxStyleProps {
    $light?: boolean
}

const ListboxInput = styled(StyledListboxInput)(() => {
    return css`
        position: relative;
        width: auto;
        justify-self: start;

        &[aria-expanded='true'] {
            box-shadow: revert;
        }
    `
})

const ListboxButton = styled(StyledListboxButton)<ListboxStyleProps>(({$light}) => {
    const lightStyle = css`
        ${text({variant: 'paragraph-large'})};
    `

    return css`
        display: grid;
        padding: ${tokens.spacing8};
        padding-left: 0;
        cursor: pointer;
        background: none;
        border: none;
        grid-template-columns: auto 1fr;
        column-gap: ${tokens.spacing8};

        &[aria-expanded='true'] {
            box-shadow: none;
        }

        ${$light && lightStyle}

        ${mq.min('md')} {
            column-gap: ${tokens.spacing14};
        }
    `
})

const ListboxOption = styled(StyledListboxOption)<ListboxStyleProps>(({$light}) => {
    const lightStyle = css`
        ${text({variant: 'paragraph-large'})}
    `

    return css`
        &,
        &[data-current] {
            display: grid;
            padding: ${tokens.spacing4} ${tokens.spacing8};

            ${$light && lightStyle}

            a:focus {
                ${focusStyles()}
                border-radius: ${tokens.arc4};
            }
        }
    `
})

const GlobalOption = styled(StyledListboxOption)`
    display: grid;
    padding: ${tokens.spacing12} ${tokens.spacing8};
    color: ${tokens.shade600};
    text-decoration: underline;

    a:focus {
        ${focusStyles()}
        border-radius: ${tokens.arc4};
    }
`

const ListboxPopover = styled(StyledListboxPopover)<ListboxStyleProps>(({$light, $direction}) => {
    const lightStyle = css`
        border: none;

        &[data-current] {
            ${text({variant: 'paragraph-large'})}
        }
    `

    return css`
        bottom: ${$direction === 'above' ? '100%' : 'unset'};
        min-width: max-content;
        padding-top: ${tokens.spacing4};
        padding-bottom: ${tokens.spacing4};
        border: solid 1px ${colors.border.secondary};
        border-radius: ${radii.arc8};
        overscroll-behavior: contain;

        ${$light && lightStyle}

        :focus {
            ${focusStyles()}
        }

        :focus-within {
            outline: none;
            box-shadow: ${tokens.shadowRaiseSubtle};
        }
    `
})

const StyledOption = styled.span`
    display: grid;
    grid-template-columns: 24px 1fr;
    align-items: center;
    column-gap: ${tokens.spacing8};
`

const StyledOptionCountry = styled.span`
    ${ellipsisStyles}
`

const StyledOptionLanguage = styled(Text)`
    grid-column: 2;
    display: none;

    ${ListboxPopover} & {
        display: block;
    }
`

export default LocaleSwitcher
